import React, { useState, useCallback, useEffect, useRef, createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { dialogHost } from '../utils/popoutWindow';
import './ConfirmDialog.css';

const ConfirmContext = createContext(null);

export const useConfirm = () => useContext(ConfirmContext);

export const ConfirmProvider = ({ children }) => {
  const { t } = useTranslation();
  const [dialog, setDialog] = useState(null);
  const okRef = useRef(null);
  const extraRef = useRef(null);

  // extraText — 세 번째 단추(선택, 2026-10-26: 프로젝트 닫기의 「저장 후 닫기」). 누르면 'extra' 로 끝난다(나머지는 예전처럼 true/false)
  const confirm = useCallback(({ title, message, confirmText, cancelText, variant, extraText }) => {
    return new Promise((resolve) => {
      const host = dialogHost();
      const doc = (host && host.ownerDocument) || document;
      setDialog({ title, message, confirmText, cancelText, variant, extraText, resolve, host, prevFocus: doc.activeElement });
    });
  }, []);

  const handleClose = useCallback((result) => {
    setDialog(d => {
      if (d?.resolve) d.resolve(result);
      // 열기 전 포커스(대개 편집기)로 돌려준다 — 안 돌려주면 확인 뒤 바로 이어 칠 수 없다
      const prev = d?.prevFocus;
      if (prev && typeof prev.focus === 'function') setTimeout(() => { try { prev.focus(); } catch (_) { /* 사라진 요소 */ } }, 0);
      return null;
    });
  }, []);

  // 키보드 — Enter = 확인(포커스가 단추에 있으면 그 단추), Esc = 취소.
  // 편집기에서 Ctrl+Enter 로 실행하면 포커스가 편집기에 남아 Enter 가 확인 단추가 아니라 **편집기에 줄바꿈으로** 들어갔다(신고 2026-11-03).
  // 열릴 때 확인 단추로 포커스를 옮기고, 그래도 다른 곳에 남은 키는 캡처 단계에서 가로챈다.
  useEffect(() => {
    if (!dialog) return undefined;
    const doc = (dialog.host && dialog.host.ownerDocument) || document;
    const focusBtn = (dialog.extraText ? extraRef.current : okRef.current);
    if (focusBtn) focusBtn.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault(); e.stopPropagation();
        handleClose(false);
      } else if (e.key === 'Enter' && !e.isComposing) {
        // 실행 단축키(Ctrl+Enter)를 누른 채 있으면 반복 입력이 위험한 실행을 그대로 확인해 버린다
        if (e.repeat) { e.preventDefault(); e.stopPropagation(); return; }
        e.preventDefault(); e.stopPropagation();
        const a = doc.activeElement;
        if (a && a.dataset && a.dataset.confirmResult) {
          const r = a.dataset.confirmResult;
          handleClose(r === 'true' ? true : r === 'extra' ? 'extra' : false);
        } else {
          handleClose(dialog.extraText ? 'extra' : true);
        }
      }
    };
    doc.addEventListener('keydown', onKey, true);
    return () => doc.removeEventListener('keydown', onKey, true);
  }, [dialog, handleClose]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {dialog && createPortal(
        <div className="confirm-overlay" onClick={() => handleClose(false)}>
          <div className="confirm-dialog" role="alertdialog" aria-modal="true" onClick={e => e.stopPropagation()}>
            <div className="confirm-header">{dialog.title || t('common:dlg.ok')}</div>
            <div className="confirm-body">{dialog.message}</div>
            <div className="confirm-footer">
              <button className="confirm-btn" data-confirm-result="false" onClick={() => handleClose(false)}>
                {dialog.cancelText || t('common:dlg.cancel')}
              </button>
              <button
                ref={okRef}
                data-confirm-result="true"
                className={`confirm-btn ${dialog.variant === 'danger' ? 'confirm-btn-danger' : 'confirm-btn-primary'}`}
                onClick={() => handleClose(true)}
              >
                {dialog.confirmText || t('common:dlg.ok')}
              </button>
              {dialog.extraText && (
                <button ref={extraRef} data-confirm-result="extra" className="confirm-btn confirm-btn-primary" onClick={() => handleClose('extra')}>
                  {dialog.extraText}
                </button>
              )}
            </div>
          </div>
        </div>,
        dialog.host || document.body
      )}
    </ConfirmContext.Provider>
  );
};

export default ConfirmProvider;
